import PublicLayout from '../../layouts/PublicLayout';
import { POLICY_VERSIONS, POLICY_LAST_UPDATED } from '../../lib/policies';

export default function CancellationPage() {
  return (
    <PublicLayout>
      <div className="container-main section-padding max-w-4xl">
        <div className="prose prose-slate max-w-none">
          <div className="mb-8 pb-6 border-b border-slate-200">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-2">
              Cancellation & Refund Policy
            </h1>
            <p className="text-slate-500">
              Last Updated: {POLICY_LAST_UPDATED} • Version {POLICY_VERSIONS.cancellation}
            </p>
          </div>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
              1. Overview
            </h2>
            <p className="text-slate-600 mb-4">
              Cancellation policies on SWFL Rentals are set by individual Hosts. Each listing 
              displays the applicable cancellation policy before you book. By completing a booking, 
              you agree to the Host's selected cancellation terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
              2. Cancellation Policy Types
            </h2>
            
            <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-4">
              <h3 className="font-semibold text-green-900 mb-2">Flexible Policy</h3>
              <ul className="text-green-800 space-y-1">
                <li>• Full refund if cancelled at least 24 hours before check-in</li>
                <li>• Cleaning fee refunded if cancelled before check-in</li>
                <li>• Service fees are non-refundable after 48 hours of booking</li>
              </ul>
            </div>
            
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 mb-4">
              <h3 className="font-semibold text-amber-900 mb-2">Moderate Policy</h3>
              <ul className="text-amber-800 space-y-1">
                <li>• Full refund if cancelled at least 5 days before check-in</li>
                <li>• 50% refund if cancelled at least 24 hours before check-in</li>
                <li>• Cleaning fee refunded if cancelled at least 5 days before</li>
                <li>• Service fees are non-refundable after 48 hours of booking</li>
              </ul>
            </div>
            
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-4">
              <h3 className="font-semibold text-red-900 mb-2">Strict Policy</h3>
              <ul className="text-red-800 space-y-1">
                <li>• Full refund only if cancelled within 48 hours of booking AND at least 14 days before check-in</li>
                <li>• 50% refund if cancelled at least 7 days before check-in</li>
                <li>• No refund if cancelled less than 7 days before check-in</li>
                <li>• Service fees are non-refundable</li>
              </ul>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
              3. How to Cancel
            </h2>
            <p className="text-slate-600 mb-4">
              To cancel a booking:
            </p>
            <ol className="list-decimal pl-6 text-slate-600 mb-4 space-y-2">
              <li>Log in to your SWFL Rentals account</li>
              <li>Go to "My Bookings" in your dashboard</li>
              <li>Select the booking you wish to cancel</li>
              <li>Click "Cancel Booking" and confirm</li>
              <li>Refund will be processed according to the policy</li>
            </ol>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
              4. Refund Processing
            </h2>
            <p className="text-slate-600 mb-4">
              Refunds are processed through Stripe to the original payment method:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Credit/debit card refunds: 5-10 business days</li>
              <li>Refund amount depends on the cancellation policy and timing</li>
              <li>You will receive email confirmation when refund is processed</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
              5. Host Cancellations
            </h2>
            <p className="text-slate-600 mb-4">
              If a Host cancels your booking:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>You will receive a full refund of all amounts paid</li>
              <li>We will help you find alternative accommodations if needed</li>
              <li>Hosts who cancel may face penalties including reduced visibility</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
              6. Force Majeure & Extenuating Circumstances
            </h2>
            <p className="text-slate-600 mb-4">
              In exceptional circumstances beyond anyone's control, special consideration may apply:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Natural disasters (hurricanes, flooding)</li>
              <li>Government travel restrictions</li>
              <li>Property damage making the listing uninhabitable</li>
              <li>Documented medical emergencies</li>
            </ul>
            <div className="bg-slate-100 border border-slate-200 rounded-lg p-4">
              <p className="text-slate-700 text-sm">
                <strong>Note:</strong> The platform may intervene in extenuating circumstances 
                but is not obligated to provide refunds beyond the Host's stated cancellation policy. 
                Each case is reviewed individually.
              </p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
              7. Dispute Resolution
            </h2>
            <p className="text-slate-600 mb-4">
              If you have concerns about a cancellation or refund:
            </p>
            <ol className="list-decimal pl-6 text-slate-600 mb-4 space-y-2">
              <li>Contact the Host directly through our messaging system</li>
              <li>If unresolved, contact SWFL Rentals support</li>
              <li>Our team will review and mediate the dispute</li>
              <li>For payment disputes, Stripe's resolution process may apply</li>
            </ol>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
              8. Modifications to Bookings
            </h2>
            <p className="text-slate-600 mb-4">
              To modify dates or details of an existing booking, you must cancel the original 
              booking (subject to the cancellation policy) and create a new booking. Contact 
              the Host to discuss alternative arrangements.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
              9. Contact
            </h2>
            <p className="text-slate-600">
              For cancellation assistance, please contact us at:<br />
              Email: support@southwestflvacationrentals.com
            </p>
          </section>
        </div>
      </div>
    </PublicLayout>
  );
}
